/**
 * Whether a stored cart-image path is a **v2 logical path**: self-contained, opening with its own store, and
 * therefore already the full blob-relative path.
 *
 * The test is "does it start with this store" rather than a shape heuristic, so a path belonging to a *different*
 * store is never mistaken for one that needs no resolving. Callers that transform v1 paths — not just prefix them
 * — need this separately from {@link cartImageContainerPath}.
 *
 * @param storeGuid Store owning the session, in the same `D` format the v2 path embeds.
 * @param storedPath The path as stored on the session.
 */
export function isSelfContainedCartImagePath(storeGuid: string, storedPath: string): boolean {
    return storedPath.replace(/^\/+/, '').startsWith(`${storeGuid}/`);
}

/**
 * Resolves a cart-image path stored on a **session** — `weightWithoutScanAlerts[].snapshotPath`,
 * `snapshotImages[].logicalPaths`, basket-item `images` — to a path relative to the `camera-images` blob
 * container, so it can be appended to the configured snapshot image URL.
 *
 * Two generations of path are in circulation and both will be for as long as historic sessions are viewable:
 *
 * - **v1** (`cartImageVersion` absent or `< 2`) stores a store-relative path such as `/cart-17/{guid}_top_3`.
 *   The store has to be prefixed to reach the blob.
 * - **v2** stores the full blob-relative path, `{storeGuid}/{yyyy-MM}/{cartId}/{sessionId}/{index}-{trigger}-…`.
 *   It is self-contained; prefixing the store again yields a doubled segment and a 404.
 *
 * Detected from the path rather than routed on the session's `cartImageVersion` on purpose: these call sites are
 * frontend components holding an alert or a path, often without the owning session, and the two formats are
 * unambiguously distinguishable — only a v2 path starts with its own store.
 *
 * @param storeGuid Store owning the session, in the same `D` format the v2 path embeds.
 * @param storedPath The path as stored on the session.
 * @returns The container-relative path, with no leading slash.
 */
export function cartImageContainerPath(storeGuid: string, storedPath: string): string {
    const path = storedPath.replace(/^\/+/, '');

    return isSelfContainedCartImagePath(storeGuid, path) ? path : `${storeGuid}/${path}`;
}
